import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route } from 'react-router-dom';
import ModalProvider from '@old/services/ModalProvider';
import ConditionalRedirects from '@old/services/ConditionalRedirects';
import AppGlobal from 'common/components/AppGlobal/AppGlobal';
import { useCustomDomainData } from 'common/hooks/useCustomDomain';
import { Enum_My_Website_Theme_Enum, Enum_Connected_Domain_State_Enum } from 'common/schema/commonSchemaRemoteTypes';
import ZeroStateDomainOffline from 'stacks/CustomDomain/ZeroStates/views/ZeroStateDomainOffline';
import { CUSTOM_DOMAIN_ROUTES } from 'routes/_definition';
import { defaultRouteProps } from 'routes/routeUtils';
import FeedToShowcaseRoutesTransition from 'common/features/ShowcaseTransition/FeedToShowcaseRoutesTransition';
import { Redirect } from 'react-router';
import { useCustomDomainRouterSetup } from 'stacks/MyWebsiteCustomize/hooks/useCustomDomainRouterSetup';
import FloatingNavigationTopNavbar from 'stacks/MyWebsiteCustomized/components/FloatingNavigationTopNavbar';
import { useGetMyWebsitePublishedSettings } from 'stacks/MyWebsiteCustomized/hooks/useGetMyWebsitePublishedSettings';
import MyWebsiteHandlePage from 'routes/publicDomain/myWebsite/[artistHandle]/_index';
import useHandleSuccessfulPayment from 'common/hooks/useHandleSuccessfulPayment';
import { useCustomDomainArtistPageSetup } from 'stacks/MyWebsiteCustomize/hooks/useCustomDomainArtistPageSetup';
import { isMyWebsiteDarkTheme } from 'utils/utilFunctions';
import { useMyWebsiteGlobalStyles } from 'stacks/MyWebsiteCustomized/hooks/useMyWebsiteGlobalStyles';
import NewsLetterDialog, {
  NEWSLETTER_DIALOG_LOCAL_STORAGE_KEY,
} from 'common/components/NewsLetterDialog/components/NewsLetterDialog';
import { newsLetterModalSliceActions } from 'store/newsLetterModal';
import { useDispatch } from 'react-redux';
import { useShouldShowFeatureFlag } from 'common/features/FeatureFlag/hooks/useFeatureFlag';
import MyWebsiteSplashScreen from 'stacks/MyWebsiteCustomize/components/CustomizeSidebar/myWebsiteSplashScreen';
import { useGetMyWebsiteCoverImages } from 'stacks/MyWebsiteCustomize/hooks/useGetMyWebsiteCoverImages';
import ArtistHandleShowroomRouter from './showroom/[title]/_router';
import ArtistAvailableArt from './available-art';
import ArtistShowcasePage from './showcase/[artworkSlug]';
import ArtistLinks from './links';
import ArtistCard from './card';

const MainPage: React.FC = () => {
  const {
    possibleProfiles,
    profile,
    isArtShowcaseRoute,
    isArtistShowroomRoute,
    isLinksRoute,
    artistHandle,
    shouldShowFloatingNavbar,
    isDarkMode,
  } = useCustomDomainRouterSetup();
  useGetMyWebsitePublishedSettings(artistHandle, profile?.id);
  useHandleSuccessfulPayment();
  const artistTheme = isDarkMode ? Enum_My_Website_Theme_Enum.Dark : Enum_My_Website_Theme_Enum.Light;
  useMyWebsiteGlobalStyles();
  useCustomDomainArtistPageSetup(profile?.context?.id, isMyWebsiteDarkTheme(artistTheme));
  const { coverImage: imagePreview, mobileCoverImage: mobilePreview } = useGetMyWebsiteCoverImages(profile?.id || 0);
  const dispatch = useDispatch();
  const [mySubscribers] = useShouldShowFeatureFlag('mySubscribers');
  const [splashScreenOpen, setSplashScreenOpen] = useState(true);

  useEffect(() => {
    const newsletterSelection = localStorage.getItem(NEWSLETTER_DIALOG_LOCAL_STORAGE_KEY + profile?.context?.id);
    if (newsletterSelection || !mySubscribers || !profile?.context?.id) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      dispatch(newsLetterModalSliceActions.OPEN_NEWS_LETTER_MODAL());
    }, 30000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [dispatch, mySubscribers, profile?.context?.id]);

  if (possibleProfiles?.length === 0) {
    return <Redirect to={CUSTOM_DOMAIN_ROUTES._404} />;
  }

  if (isArtistShowroomRoute) {
    return (
      <Route {...defaultRouteProps.subrouter} path={[CUSTOM_DOMAIN_ROUTES.SHOWROOM_TITLE]}>
        <ArtistHandleShowroomRouter />
      </Route>
    );
  }

  if (isLinksRoute) {
    return (
      <Route {...defaultRouteProps.terminalRoute} path={[CUSTOM_DOMAIN_ROUTES.LINKS]}>
        <ArtistLinks />
      </Route>
    );
  }

  const handleSplashScreenOnClose = () => {
    setSplashScreenOpen(false);
  };

  return (
    <>
      {shouldShowFloatingNavbar && <FloatingNavigationTopNavbar artistHandle={artistHandle ?? ''} />}
      {profile?.context?.id && <NewsLetterDialog contextId={profile.context.id} />}

      {splashScreenOpen && imagePreview && mobilePreview && (
        <MyWebsiteSplashScreen
          image={imagePreview}
          mobileImage={mobilePreview}
          contextId={profile?.context?.id || 0}
          onClose={handleSplashScreenOnClose}
        />
      )}

      <FeedToShowcaseRoutesTransition feedKey={artistHandle} isOnShowcase={isArtShowcaseRoute}>
        <Route {...defaultRouteProps.terminalRoute} path={[CUSTOM_DOMAIN_ROUTES.SHOWCASE_ARTWORK_SLUG]}>
          <ArtistShowcasePage />
        </Route>

        <>
          <MyWebsiteHandlePage
            artistHandle={artistHandle}
            artistContextId={profile?.context?.id}
            artistTheme={artistTheme}
          />
          <Route {...defaultRouteProps.terminalRoute} path={[CUSTOM_DOMAIN_ROUTES.CARD]}>
            <ArtistCard />
          </Route>
          <Route {...defaultRouteProps.terminalRoute} path={[CUSTOM_DOMAIN_ROUTES.AVAILABLE_ART]}>
            <ArtistAvailableArt artistContextId={profile?.context?.id} />
          </Route>
        </>
      </FeedToShowcaseRoutesTransition>
    </>
  );
};

const AppRouter: React.FC = () => {
  const { domain } = useCustomDomainData();
  const isDomainDisabledOrInvalid =
    !domain?.isLoading && (!domain || domain.status !== Enum_Connected_Domain_State_Enum.Active);

  if (isDomainDisabledOrInvalid) {
    return (
      <>
        <AppGlobal.Effects />
        <AppGlobal.Components />
        <Route path="*" component={ZeroStateDomainOffline} />
        <ModalProvider />
      </>
    );
  }

  return (
    <Router>
      <AppGlobal.Effects />
      <AppGlobal.Components />
      {isDomainDisabledOrInvalid && <Route path="/" component={ZeroStateDomainOffline} />}
      {!isDomainDisabledOrInvalid && <MainPage />}
      <ModalProvider />
      <ConditionalRedirects />
    </Router>
  );
};

export default AppRouter;
